// See TimePicklist.spec.md for TPL-FR-001 and TPL-FR-003
//
// Shared "Last N minutes/hours/days" dropdown. It only lets the user pick a range and reports
// the range key; to get the matching start/end times, call getTimeRange from ./timeRanges when
// fetching data.

import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { useMemo } from 'react'
import AsyncDropdown from 'core/elements/dropdown/AsyncDropdown'
import {
  DEFAULT_TIME_RANGE_OPTIONS,
  getTimeRangeLabel,
  TIME_RANGES,
  type TimeRange,
} from './timeRanges'

interface Props {
  currentTimeRange?: TimeRange
  onChange: (timeRange: TimeRange) => void
  // Ranges to offer, in display order; defaults to DEFAULT_TIME_RANGE_OPTIONS
  options?: TimeRange[]
  label?: string
  className?: string
  // TPL-FR-001: Which edge of the field the options menu lines up with (see Dropdown)
  menuAlign?: 'left' | 'right'
}

export default function TimePicklist({
  currentTimeRange = TIME_RANGES.last_1_hour,
  onChange,
  options = DEFAULT_TIME_RANGE_OPTIONS,
  label,
  className,
  menuAlign,
}: Props) {
  const classes = useStyles()
  // TPL-FR-001: Offer only the ranges this page asked for
  const items = useMemo(
    () => options.map((value) => ({ label: getTimeRangeLabel(value), value })),
    [options],
  )

  // TPL-FR-003: Report the picked range once per selection (never on mount)
  return (
    <AsyncDropdown<TimeRange>
      compact
      label={label}
      items={items}
      value={currentTimeRange}
      // The dropdown's type also allows its "all"/"none" sentinels, which this picker never shows
      onChange={(value) => onChange(value as TimeRange)}
      menuAlign={menuAlign}
      className={clsx(classes.customDropdown, className)}
    />
  )
}

const useStyles = makeStyles({
  customDropdown: {
    '& .placeholder': {
      fontWeight: 400,
    },
    '& .dropdownMenu': {
      maxHeight: 250,
    },
  },
})
